import React from 'react';
import {
  CardIdentityFieldsFragment,
  CommandRequestStatus,
  ProjectCommand,
  ProjectIntention,
  ProjectTriggerFieldsFragment,
} from '../../../../../../../generated/core-api/graphql-react-query';

interface ProjectTrigger {
  __typename?: string;
  command?: { text: string };
  intention?: { text: string };
}

export function isProjectCommand(projectTrigger: ProjectTrigger | undefined): projectTrigger is ProjectCommand {
  return projectTrigger?.__typename === 'ProjectCommand';
}

export function isProjectIntention(projectTrigger: ProjectTrigger | undefined): projectTrigger is ProjectIntention {
  return projectTrigger?.__typename === 'ProjectIntention';
}

export function getProjectTrigger(projectTrigger: ProjectTrigger): { id: string; text: string } | null {
  if (isProjectCommand(projectTrigger)) {
    return { id: projectTrigger.command.id, text: projectTrigger.command.text };
  }
  if (isProjectIntention(projectTrigger)) {
    return { id: projectTrigger.intention.id, text: projectTrigger.intention.text };
  }
  return null;
}

type Display = { context: string; display: CardIdentityFieldsFragment };
export type EnrichedProjectTrigger<T extends ProjectTriggerFieldsFragment> = T & { displays: Display[] };

function extendCommand<T extends ProjectTriggerFieldsFragment>(command: T): EnrichedProjectTrigger<T> {
  const { card, contextualDisplays } = command;
  const defaultDisplay = card ? [{ context: 'default', display: card }] : [];
  const extraDisplays = contextualDisplays.map((context) => ({
    context: context.commandContextTextLower,
    display: context.card,
  }));
  return {
    ...command,
    displays: [...defaultDisplay, ...extraDisplays],
  };
}

function isReady(command: ProjectTriggerFieldsFragment): boolean {
  if (command.deletedAt) return false;
  if (isProjectIntention(command)) return true;
  if (isProjectCommand(command)) {
    if (!command.command.commandRequest) return true;
    switch (command.command.commandRequest.status) {
      case CommandRequestStatus.Ok:
      case CommandRequestStatus.Generic:
        return true;
      default:
        return false;
    }
  }
  return false;
}

function interactsWithUser(command: ProjectTriggerFieldsFragment & { displays: Display[] }): boolean {
  if (command.displays.length > 0) return true;
  const minDuration = command.topic?.mandatoryTopic?.minimumDuration;
  if (minDuration != null) return true;
  if (command.commandContextTextLower) return true;
  return false;
}

export default function useCommands(commands: ProjectTriggerFieldsFragment[], search: string | null): (ProjectTriggerFieldsFragment & { displays: Display[] })[] {
  return React.useMemo(() => {
    const sortedCommands = commands.sort((a, b) => getProjectTrigger(a)?.text.localeCompare(getProjectTrigger(b)?.text));

    const extendedCommands = sortedCommands.map((command) => extendCommand(command));
    const listedCommands = extendedCommands.filter((command) => isReady(command) && interactsWithUser(command));
    if (!search) return listedCommands;
    return listedCommands.filter((command) => getProjectTrigger(command)?.text.toLowerCase().includes(search.toLowerCase()));
  }, [commands, search]);
}
